/**
 * Generate Episode View
 *
 * Main episode generation page with:
 * - Idea Generator integration
 * - Episode generation form
 * - Loading states
 * - Episode preview/edit
 * - LLM-assisted refinement
 * - Episode creation
 *
 * Story: 11.5 - Basic Episode Generator
 * Acceptance Criteria: AC 4 - Task 10
 */

import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { EpisodePreview } from '../components/EpisodePreview';
import { RefinementPanel } from '../components/RefinementPanel';
import { useCreateEpisode } from '../services/episodesApi';
import {
  useGenerateEpisode,
  type GeneratedEpisode,
  type GenerationMetadata,
} from '../services/generationApi';
import { useInitialData } from 'contexts/InitialDataContext';
import { useLanguage } from 'contexts/LanguageContext';
import {
  getSkillLevelEnglishName,
  getEpisodeDurationEnglishName,
  getTagEnglishName,
} from 'services/initialDataApi';

// ============================================================================
// Types
// ============================================================================

interface GenerateEpisodeFormData {
  idea: string;
  complexity?: number; // Single complexity level (1-5)
  tags?: number[]; // Story 15.8: Integer tag IDs
  durationId?: number; // Story 15.11: Duration ID (1-5)
  titleOverride?: string; // Optional title to override LLM-generated name
}

interface SelectedIdeaData {
  idea: string;
  selectedTagIds: number[]; // Story 15.8: Integer tag IDs selected in filter at generation time
  suggestedComplexity: string; // CEFR code: A1, A2, B1, B2, C1
  suggestedDuration: string; // Name: Micro, Short, Medium, Long, Extended
}

type ViewState = 'form' | 'loading' | 'preview';

// ============================================================================
// Component
// ============================================================================

export function GenerateEpisodeView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewState, setViewState] = useState<ViewState>('form');
  const [generatedEpisode, setGeneratedEpisode] = useState<GeneratedEpisode | null>(null);
  const [generationMetadata, setGenerationMetadata] = useState<GenerationMetadata | null>(null);
  const [preGeneratedEpisodeId, setPreGeneratedEpisodeId] = useState<string | null>(null); // Story 3.11
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const urlParamsProcessedRef = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<GenerateEpisodeFormData>({
    mode: 'onChange',
    defaultValues: {
      idea: '',
      complexity: undefined, // Single complexity level
      tags: [],
      durationId: undefined, // Story 15.11
    },
  });

  const { state: initialDataState } = useInitialData();
  const { selectedLanguage } = useLanguage(); // Use global language from header
  const skillLevels = initialDataState.data?.skillLevels || [];
  const episodeDurations = initialDataState.data?.episodeDurations || []; // Story 15.11
  const tags = initialDataState.data?.tags || []; // Get all tags from initial data
  const generateEpisodeMutation = useGenerateEpisode();
  const createEpisodeMutation = useCreateEpisode();

  const ideaValue = watch('idea');
  const selectedTags = watch('tags');
  const [searchParams] = useSearchParams();

  // Pre-fill form from URL params (when opening in new tab from Idea Generator)
  useEffect(() => {
    // Wait for initial data to load before setting form values
    if (!initialDataState.data) return;
    // Only process URL params once
    if (urlParamsProcessedRef.current) return;

    const idea = searchParams.get('idea');
    const complexity = searchParams.get('complexity');
    const durationId = searchParams.get('duration_id');
    const tagParams = searchParams.getAll('tags');

    if (idea) {
      urlParamsProcessedRef.current = true;
      setValue('idea', idea, { shouldValidate: true });

      // Set advanced options if any params are set
      if (complexity || durationId || tagParams.length > 0) {
        // Set complexity (skill level)
        if (complexity) {
          const complexityNum = parseInt(complexity, 10);
          if (complexityNum >= 1 && complexityNum <= 5) {
            setValue('complexity', complexityNum, { shouldValidate: true });
          }
        }

        // Set duration
        if (durationId) {
          const durationNum = parseInt(durationId, 10);
          if (durationNum >= 1 && durationNum <= 5) {
            setValue('durationId', durationNum, { shouldValidate: true });
          }
        }

        // Set tags — pass strings to match native <select> option values
        if (tagParams.length > 0) {
          setValue('tags', tagParams as unknown as number[], {
            shouldValidate: true,
          });
        }
      }

      // Clear URL params after form is filled
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataState.data]);

  // Pre-fill form from navigation state (legacy: when using navigate() with state)
  useEffect(() => {
    const state = location.state as { selectedIdea?: SelectedIdeaData };
    if (state?.selectedIdea) {
      handleIdeaSelected(state.selectedIdea);
      // Clear the navigation state to prevent re-filling on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleIdeaSelected = (ideaData: SelectedIdeaData) => {
    // Auto-fill idea textarea with selected idea
    setValue('idea', ideaData.idea, { shouldValidate: true });

    // Auto-fill suggested complexity (convert CEFR code to skill level ID)
    if (ideaData.suggestedComplexity) {
      const matchingLevel = skillLevels.find(
        (sl) => sl.cefr_code.toUpperCase() === ideaData.suggestedComplexity.toUpperCase()
      );

      if (matchingLevel) {
        setValue('complexity', matchingLevel.id, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }

    // Story 15.11: Auto-fill suggested duration (convert name to duration ID)
    if (ideaData.suggestedDuration) {
      const matchingDuration = episodeDurations.find((duration) => {
        const durationName = getEpisodeDurationEnglishName(duration);
        return durationName.toLowerCase() === ideaData.suggestedDuration.toLowerCase();
      });

      if (matchingDuration) {
        setValue('durationId', matchingDuration.id, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }

    // Auto-fill tags if they were selected during idea generation
    if (ideaData.selectedTagIds.length > 0) {
      setValue('tags', ideaData.selectedTagIds.map(String) as unknown as number[], {
        shouldValidate: true,
      });
    }

    toast.success('Idea selected! Form fields have been auto-filled.');
  };

  const handleCancel = () => {
    // Abort the ongoing HTTP request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Reset mutation state and UI
    generateEpisodeMutation.reset();
    setViewState('form');
    toast('Episode generation cancelled');
  };

  const handleEpisodeChange = (updatedEpisode: GeneratedEpisode) => {
    setGeneratedEpisode(updatedEpisode);
  };

  const handleEpisodeRefined = (refinedEpisode: GeneratedEpisode, changesMade: string[]) => {
    setGeneratedEpisode(refinedEpisode);
    console.log('Changes made:', changesMade);
  };

  const handleCreateEpisode = () => {
    if (!generatedEpisode) return;

    // Validate required fields
    // Story 3.9.15: description removed - episodes are now language-specific
    if (!generatedEpisode.name || !generatedEpisode.context) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (generatedEpisode.goals.length < 1 || generatedEpisode.goals.length > 10) {
      toast.error('Episode must have between 1 and 10 goals');
      return;
    }

    if (generatedEpisode.personas.length < 2 || generatedEpisode.personas.length > 5) {
      toast.error('Episode must have between 2 and 5 personas');
      return;
    }

    if (generatedEpisode.personas[0].name.toLowerCase() !== 'narrator') {
      toast.error('First persona must be "Narrator"');
      return;
    }

    // Convert to API format
    // Story 15.11: durationId is now an integer (1-4)
    // Story 3.9.15: Episodes are language-specific, use languageCode from global context
    // Story 3.11: Include visual assets, dictionary, and pre-generated ID
    const episodeData = {
      id: preGeneratedEpisodeId || undefined, // Story 3.11: Use pre-generated UUID to match image assets
      name: generatedEpisode.name,
      languageCode: selectedLanguage, // Use global language from header
      context: generatedEpisode.context,
      goals: generatedEpisode.goals,
      personas: generatedEpisode.personas.map((p) => ({
        name: p.name,
        role: p.role || p.name,
        description: p.description,
        thumbnailUrl: p.thumbnailUrl || null, // Story 3.11: Persona avatar
      })),
      scenario: generatedEpisode.scenario,
      durationId: generatedEpisode.durationId || 2, // Story 15.11: Default to Medium (2)
      complexityLevel: generatedEpisode.complexityLevel || 3, // Default to Intermediate
      tagIds: generatedEpisode.tagIds || [],
      // Story 3.11: Visual assets
      thumbnailUrl: generatedEpisode.thumbnailUrl || null,
      backgroundImageUrl: generatedEpisode.backgroundImageUrl || null,
      // Story 3.11: Dictionary
      dictionary:
        generatedEpisode.dictionary?.map((d) => ({
          word: d.word,
          explanation: d.explanation,
          examplePhrases: d.examplePhrases,
        })) || null,
    };

    createEpisodeMutation.mutate(episodeData, {
      onSuccess: () => {
        toast.success('Episode created successfully!');
        // Navigate to episodes list or episode edit page
        navigate('/episodes');
      },
      onError: (error: unknown) => {
        const err = error as {
          response?: { data?: { error?: { message?: string } } };
          message?: string;
        };
        const errorMessage =
          err.response?.data?.error?.message || err.message || 'Failed to create episode';
        toast.error(errorMessage);
      },
    });
  };

  const handleDiscardAndStartOver = () => {
    setShowDiscardConfirm(true);
  };

  const confirmDiscard = () => {
    setGeneratedEpisode(null);
    setGenerationMetadata(null);
    setPreGeneratedEpisodeId(null); // Story 3.11: Clear pre-generated UUID
    setViewState('form');
    setShowDiscardConfirm(false);
    toast.success('Episode discarded. Starting over...');
  };

  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
  };

  const handleBackToForm = () => {
    // Save episode preview in component state (it's already there)
    setViewState('form');
    toast('Returned to form. Episode preview is preserved.');
  };

  const onSubmit = async (data: GenerateEpisodeFormData) => {
    setViewState('loading');

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    // Story 15.11: duration changed to durationId
    generateEpisodeMutation.mutate(
      {
        idea: data.idea,
        language: selectedLanguage, // Use global language from header
        complexity: data.complexity, // Single complexity level
        tags: data.tags,
        durationId: data.durationId, // Story 15.11
        titleOverride: data.titleOverride || undefined,
        signal: abortControllerRef.current.signal, // Pass abort signal for cancellation
      },
      {
        onSuccess: (response) => {
          abortControllerRef.current = null;
          setGeneratedEpisode(response.episode);
          setGenerationMetadata(response.generationMetadata);
          setPreGeneratedEpisodeId(response.episodeId); // Story 3.11: Store pre-generated UUID
          setViewState('preview');
          toast.success('Episode generated successfully!');
        },
        onError: (error: unknown) => {
          abortControllerRef.current = null;
          // Don't show error toast if request was intentionally cancelled
          const err = error as {
            response?: { data?: { error?: { message?: string } } };
            message?: string;
            code?: string;
          };
          if (err.code === 'ERR_CANCELED' || err.message === 'canceled') {
            // Request was cancelled by user, already handled in handleCancel
            return;
          }
          setViewState('form');
          const errorMessage =
            err.response?.data?.error?.message || err.message || 'Failed to generate episode';
          toast.error(errorMessage);
        },
      }
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold text-gray-900">Generate Episode</h1>
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            {initialDataState.data?.languages.find((l) => l.code === selectedLanguage)?.name ||
              selectedLanguage.toUpperCase()}
          </span>
        </div>
        <Link to="/episodes" className="text-blue-600 hover:text-blue-800 font-medium">
          ← Back to Episodes
        </Link>
      </div>

      {/* Loading State */}
      {viewState === 'loading' && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center" data-testid="loading-state">
          <div className="flex flex-col items-center justify-center space-y-4">
            {/* Spinner */}
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>

            {/* Loading Message */}
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-gray-800">Generating episode...</h2>
              <p className="text-gray-600">This may take 2-5 minutes</p>
              <p className="text-sm text-gray-500">
                Running Generator-Critique loop to create high-quality episode...
              </p>
            </div>

            {/* Cancel Button */}
            <button
              type="button"
              onClick={handleCancel}
              className="mt-6 px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Preview State with EpisodePreview and RefinementPanel */}
      {viewState === 'preview' && generatedEpisode && generationMetadata && (
        <div data-testid="preview-state" className="space-y-6">
          <EpisodePreview
            episode={generatedEpisode}
            metadata={generationMetadata}
            tags={tags.map((t) => ({ id: t.id, name: getTagEnglishName(t) }))}
            onChange={handleEpisodeChange}
          />

          <RefinementPanel episode={generatedEpisode} onEpisodeRefined={handleEpisodeRefined} />

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleCreateEpisode}
                disabled={createEpisodeMutation.isPending}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                data-testid="create-episode-button"
              >
                {createEpisodeMutation.isPending ? 'Creating...' : 'Create Episode'}
              </button>

              <button
                type="button"
                onClick={handleBackToForm}
                className="px-6 py-3 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 transition-colors"
                data-testid="back-to-form-button"
              >
                Back to Form
              </button>

              <button
                type="button"
                onClick={handleDiscardAndStartOver}
                className="px-6 py-3 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 transition-colors"
                data-testid="discard-button"
              >
                Discard & Start Over
              </button>
            </div>
          </div>

          {/* Discard Confirmation Modal */}
          {showDiscardConfirm && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              data-testid="discard-modal"
            >
              <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                <h3 className="text-lg font-bold mb-4">Discard Episode?</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to discard this episode and start over? All changes will be
                  lost.
                </p>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={confirmDiscard}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    data-testid="confirm-discard-button"
                  >
                    Yes, Discard
                  </button>
                  <button
                    type="button"
                    onClick={cancelDiscard}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    data-testid="cancel-discard-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form State */}
      {viewState === 'form' && (
        <>
          {/* Generation Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-md p-6">
            {/* Idea Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Episode Idea / Description *
              </label>
              <textarea
                {...register('idea', {
                  required: 'Episode idea is required',
                  maxLength: { value: 1000, message: 'Maximum 1000 characters' },
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.idea ? 'border-red-500' : 'border-gray-300'
                }`}
                rows={4}
                placeholder="Describe your episode idea (e.g., 'Practice ordering food at a restaurant')"
                data-testid="idea-input"
              />
              <div className="flex justify-between items-center mt-1">
                <div>
                  {errors.idea && (
                    <p className="text-red-500 text-sm" data-testid="idea-error">
                      {errors.idea.message}
                    </p>
                  )}
                </div>
                <p className="text-sm text-gray-500" data-testid="character-counter">
                  {ideaValue?.length || 0} / 1000
                </p>
              </div>
            </div>

            {/* Title Override */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title Override (optional)
              </label>
              <input
                type="text"
                {...register('titleOverride', {
                  maxLength: { value: 200, message: 'Maximum 200 characters' },
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.titleOverride ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Leave empty to use AI-generated title"
                data-testid="title-override-input"
              />
              {errors.titleOverride && (
                <p className="text-red-500 text-sm mt-1">{errors.titleOverride.message}</p>
              )}
            </div>

            {/* Advanced Options */}
            <div className="space-y-4 mb-6" data-testid="advanced-toggle">
              {/* Complexity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Complexity Level (optional)
                </label>
                <select
                  {...register('complexity', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="complexity-select"
                >
                  <option value="">Select complexity...</option>
                  {skillLevels.map((skillLevel) => (
                    <option key={skillLevel.id} value={skillLevel.id}>
                      {getSkillLevelEnglishName(skillLevel)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (optional)
                  {selectedTags?.length
                    ? ` — ${selectedTags.length} selected`
                    : ` — ${tags.length} available`}
                </label>
                <select
                  {...register('tags')}
                  multiple
                  size={Math.min(tags.length, 10)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="tags-select"
                >
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {getTagEnglishName(tag)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
              </div>

              {/* Duration - Story 15.11: Duration ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (optional)
                </label>
                <select
                  {...register('durationId', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="duration-select"
                >
                  <option value="">Select duration...</option>
                  {episodeDurations
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((duration) => (
                      <option key={duration.id} value={duration.id}>
                        {getEpisodeDurationEnglishName(duration)}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Generate Button */}
            <div>
              <button
                type="submit"
                disabled={!isValid}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                data-testid="generate-button"
              >
                Generate Episode
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
